import { useQueryClient } from "@tanstack/react-query";

import { ItemIconChain } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { ValueType } from "app/pages/activation/components/Shared/TrendChangeTooltip/type";
import { Trends } from "app/pages/activation/components/Topics/Topic/Trends/Trends";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { ActivationUnits, addPercent } from "app/pages/activation/helpers/format";
import { updateBooleanInActivationItem } from "app/pages/activation/helpers/home/updateBooleanInActivationItem";
import { updateBooleanInActivationMetadata } from "app/pages/activation/helpers/home/updateBooleanInActivationMetadata";
import { prepareTrendData } from "app/pages/activation/helpers/topics/dataGenerators";
import { TABS_ARRAY } from "app/pages/activation/modules/Settings/config";
import { SettingsTabs } from "app/pages/activation/modules/Settings/types";
import {
    ACTIVATION_PROJECT_ROUTE,
    ACTIVATION_SETTINGS_ROUTE,
    DEFAULT_ACTIVATION_GRANULARITY,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { ActivationSetSchema } from "app/pages/activation/types/api";
import { useSubscribeToAllAlertsMutation } from "app/pages/activation-alerts/queries/alerts/useSubscribeToAllAlertsMutation";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { GATriggerWithPropertyMapping } from "../../ga-connect";
import { GA_SOURCE } from "../../GAWidget/widgetsConfig";
import { AlertsIndication } from "../../AlertsIndication/AlertsIndication";
import { Share } from "../../Topics/Topic/Share/Share";

import { itemKeys } from "./automationKeys";
import { HomeContextMenuOptions } from "./Components/ContextMenu/config";
import { ContextMenu, type ContextMenuSchema } from "./Components/ContextMenu/ContextMenu";
import { NameSection } from "./Components/NameSection/NameSection";
import * as SC from "./styles";
import { SHARE_TITLE } from "./texts";

type ItemSchema = Pick<ContextMenuSchema, "isGaConnectHidden"> & {
    project: ActivationSetSchema;
    isLoading: boolean;
    isContextMenuLoading?: boolean;
    isGaConnected?: boolean;
    onConnectGa?: () => void;
    onSubscribe?: () => void;
    icons: string[];
};

const ItemBase = ({
    project: {
        id: activationId,
        name,
        country,
        primaryDomain,
        isOwn,
        shareTrend,
        share,
        periodOverPeriodShareDifference,
        competitorsDomains,
        sharedBy,
        isSubscribed,
        isFavorite,
        hasNewAlerts,
    },
    isLoading,
    isContextMenuLoading,
    isGaConnected,
    isGaConnectHidden,
    onConnectGa,
    onSubscribe,
    icons,
}: ItemSchema) => {
    const track = activationTracking.homepage;

    const trendData = prepareTrendData({
        data: shareTrend,
        granularity: DEFAULT_ACTIVATION_GRANULARITY,
        valueType: ValueType.Percentage,
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isAlertsAllowed = Boolean(swSettings.components?.Alerts?.isAllowed);

    const onClick = async () => {
        await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: activationId,
            country,
            webSource: ActivationComponentAssets.defaultSource,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
        });
    };

    const onClickContextMenu = async ({ id }: { id: HomeContextMenuOptions }) => {
        track.trackContextMenu();

        if (id === HomeContextMenuOptions.Settings) {
            track.trackProjectSettings();
            await proNavigator.go(ACTIVATION_SETTINGS_ROUTE, {
                key: activationId,
                country,
                webSource: ActivationComponentAssets.defaultSource,
                timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
                domain: primaryDomain,
                tab: !isOwn ? TABS_ARRAY.indexOf(SettingsTabs.Subscriptions) : TABS_ARRAY.indexOf(SettingsTabs.General),
            });
        }
        if (id === HomeContextMenuOptions.ConnectGa) {
            track.trackConnectGa();
            onConnectGa?.();
        }
        if (id === HomeContextMenuOptions.Subscribe) {
            if (!isAlertsAllowed) {
                return;
            }

            track.trackSubscribe("menu");
            onSubscribe();
        }
    };

    return (
        <SC.ProjectWrapper
            onClick={onClick}
            data-automation={itemKeys.container}
        >
            <SC.NameGroup>
                <SC.IconsGroupContainer>
                    <ItemIconChain
                        icons={icons}
                        size={24}
                        maxIconChain={2}
                        counterBoxWidth={24}
                    />
                </SC.IconsGroupContainer>

                <NameSection
                    isFavorite={isFavorite}
                    isGaConnected={isGaConnected}
                    isSubscribed={isSubscribed}
                    activationId={activationId}
                    isLoading={isLoading}
                    name={name}
                    country={country}
                    domain={primaryDomain}
                    competitors={competitorsDomains}
                    sharedBy={sharedBy}
                    onSubscribe={onSubscribe}
                    isOwn={isOwn}
                />
            </SC.NameGroup>

            <AlertsIndication
                hasNewAlerts={hasNewAlerts}
                activationId={activationId}
                country={country}
            />

            <SC.Separator />

            <SC.ShareContainer>
                <Share
                    isLoading={isLoading}
                    value={addPercent(percentageFilter()(share, 1) as string)}
                    change={periodOverPeriodShareDifference}
                    label={translate(SHARE_TITLE)}
                    isDisabled={false}
                    metric={ActivationUnits.PercentPoints}
                    name="share"
                />
            </SC.ShareContainer>

            <Trends
                trendData={trendData}
                isLoading={isLoading}
            />

            <ContextMenu
                isSubscribed={isSubscribed}
                activationId={activationId}
                isLast={false}
                isLoading={isLoading || isContextMenuLoading}
                onClick={onClickContextMenu}
                isAlertsAllowed={isAlertsAllowed}
                isGaConnectHidden={isGaConnectHidden}
            />
        </SC.ProjectWrapper>
    );
};

/**
 * Important note! GAContextProviders used inside GATriggerWithPropertyMapping wrap content
 * with their own query client at the moment, so we need to move out all query-related stuff to use the proper client.
 */
export const Item = (props: Omit<ItemSchema, "onSubscribe" | "isGAConnectionsListLoading">) => {
    const { canEdit, id: activationId, trafficSource, isFavorite } = props.project;
    const queryClient = useQueryClient();
    const { mutate: subscribe } = useSubscribeToAllAlertsMutation({ subscribeToBenchmark: true });

    const onSubscribe = () => {
        subscribe(
            { activationId },
            {
                onSuccess: () => {
                    void updateBooleanInActivationItem({
                        queryClient,
                        itemId: activationId,
                        objectKey: "isSubscribed",
                        value: !isFavorite,
                    });
                    updateBooleanInActivationMetadata({
                        queryClient,
                        itemId: activationId,
                        objectKey: "isSubscribed",
                        value: true,
                    });
                },
            },
        );
    };

    const isGaConnected = trafficSource === GA_SOURCE;

    // We need to hide the GA connect button in case of GA or SW data already connected
    // as there is no simple way for now to check if GA mapping already exists
    const isGaConnectHidden = props.isLoading || !!trafficSource || !canEdit;

    if (isGaConnectHidden) {
        return (
            <ItemBase
                {...props}
                isGaConnected={isGaConnected}
                isGaConnectHidden
                onSubscribe={onSubscribe}
            />
        );
    }

    return (
        <GATriggerWithPropertyMapping activationId={activationId}>
            {({ open, isLoading: isGAConnectionsListLoading }) => (
                <ItemBase
                    {...props}
                    onSubscribe={onSubscribe}
                    onConnectGa={open}
                    isContextMenuLoading={isGAConnectionsListLoading}
                    isGaConnected={isGaConnected}
                />
            )}
        </GATriggerWithPropertyMapping>
    );
};
